import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { roomService } from '../services/roomService';

const { FiTrash2, FiClock, FiRefreshCw, FiAlertCircle } = FiIcons;

const CleanupMonitor = () => {
  const [cleanupStats, setCleanupStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCleanup, setLastCleanup] = useState(null);

  const fetchCleanupStats = async () => {
    setIsLoading(true);
    try {
      const result = await roomService.getCleanupStats();
      if (result.success) {
        setCleanupStats(result.stats);
        setLastCleanup(new Date());
      }
    } catch (error) {
      console.error('Error fetching cleanup stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCleanupStats();
    // Check cleanup stats every 30 seconds
    const interval = setInterval(fetchCleanupStats, 30000);
    return () => clearInterval(interval);
  }, []);

  if (!cleanupStats) return null;

  return (
    <motion.div
      className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <SafeIcon icon={FiTrash2} className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-900">Room Cleanup Status</span>
        <button
          onClick={fetchCleanupStats}
          disabled={isLoading}
          className="ml-auto p-1 hover:bg-gray-100 rounded"
        >
          <SafeIcon 
            icon={FiRefreshCw} 
            className={`w-3 h-3 text-gray-500 ${isLoading ? 'animate-spin' : ''}`} 
          />
        </button>
      </div>
      
      <div className="space-y-2 text-xs text-gray-600">
        <div className="flex items-center justify-between">
          <span>Last cleanup:</span>
          <span className="font-mono">
            {lastCleanup ? lastCleanup.toLocaleTimeString() : 'Never'}
          </span>
        </div>
        
        {cleanupStats && cleanupStats.length > 0 && (
          <div className="flex items-center justify-between">
            <span>Rooms deleted:</span>
            <span className="font-mono text-red-600">
              {cleanupStats[0]?.deleted_count || 0}
            </span>
          </div>
        )}
        
        <div className="flex items-center gap-1 text-green-600">
          <SafeIcon icon={FiClock} className="w-3 h-3" />
          <span>Auto-cleanup: Every minute</span>
        </div>
      </div>
      
      <div className="mt-3 pt-2 border-t border-gray-100">
        <div className="flex items-center gap-1 text-xs text-amber-600">
          <SafeIcon icon={FiAlertCircle} className="w-3 h-3" />
          <span>Rooms auto-delete after 1 hour</span>
        </div>
      </div>
    </motion.div>
  );
};

export default CleanupMonitor;